'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { FormRenderer } from '@jlg-website/form/renderer/client';
import type {
  Answers,
  FormConfig,
  FormInput,
  FormLocale,
  FormSubmitPayload,
} from '@jlg-website/shared';
import { useLeadForm } from '@/hooks/use-lead-form';
import { buildAnswersQuery } from '@/components/pages/form/answer-query';
import { MASS_TORT_VARIANT } from './constants';

type MassTortFormClientProps = {
  schema: FormInput;
  config?: FormConfig;
  locale?: FormLocale;
};

/**
 * Mass-tort hero form. Renders the *same* engine — and fires the *same* lead/marketing events (via
 * `useLeadForm`) — as the page's full form, but with its own flow: `autoNext` is on, and a
 * multi-step form is only ever answered up to its first step here. Selecting an answer auto-advances,
 * which hands the visitor over to `/forms/<page-slug>` with the first step's answers in the query,
 * where the full form pre-selects them and carries on. A single-step form is the last step, so it
 * does not auto-advance: the visitor clicks to submit, inline, exactly like the default client.
 */
export default function MassTortFormClient({ schema, config, locale }: MassTortFormClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    config: configWithValidators,
    handleSubmit,
    handleStepNext,
    handleReachLastStep,
  } = useLeadForm(schema, config);

  // The hand-off target: the full form for this same page. The query string (the first step's
  // answers) is only known at hand-off, but prefetch is keyed by route segments, not the query — so
  // warming the pathname now downloads the form's chunks ahead of the redirect regardless of which
  // answers get appended later.
  const formRoute = `/forms${pathname}`;
  useEffect(() => {
    router.prefetch(formRoute);
  }, [router, formRoute]);

  // Wraps the live form so its rendered height can be captured the instant before hand-off, letting
  // the placeholder hold the card at exactly the size it had on step one (no squashing).
  const contentRef = useRef<HTMLDivElement>(null);
  const [handoffHeight, setHandoffHeight] = useState<number | null>(null);

  // Once the visitor advances past step one, the card is swapped for a spinner placeholder while the
  // browser navigates to the full form — so the hero can no longer be answered.
  const [isHandingOff, setIsHandingOff] = useState(false);

  // Fire the shared progress event, then (multi-step only) hand off. Guarded to the first step; in
  // practice we redirect before the engine reaches a second one.
  const onStepNext = useCallback(
    (payload: FormSubmitPayload): void => {
      handleStepNext(payload);

      if (payload.current_step !== 1) return;

      const firstStepAnswers: Answers = {};
      Object.assign(firstStepAnswers, payload.answers);

      setHandoffHeight(contentRef.current?.offsetHeight ?? null);
      setIsHandingOff(true);
      router.push(`/forms${pathname}${buildAnswersQuery(firstStepAnswers)}`);
    },
    [handleStepNext, router, pathname]
  );

  if (isHandingOff) {
    return (
      <div
        data-mass-tort-handoff=""
        aria-busy="true"
        style={handoffHeight ? { minHeight: handoffHeight } : undefined}
      >
        <HandoffSpinner />
      </div>
    );
  }

  return (
    <div ref={contentRef}>
      <FormRenderer
        schema={schema}
        config={{ ...configWithValidators }}
        variant={MASS_TORT_VARIANT}
        locale={locale}
        autoNext
        // Single-step hero: the last-step button is the submit CTA, labelled like the collapsed
        // "Start free case review" button it sits behind. Multi-step steps advance with "See if I have
        // a case".
        labels={{ next: 'See if I have a case', finish: 'Start free case review' }}
        onSubmit={handleSubmit}
        onStepNext={onStepNext}
        onReachLastStep={handleReachLastStep}
        enableNextOnValidate={false}
      />
    </div>
  );
}

/**
 * Local loading spinner shown in the hand-off placeholder: a single 3/4 SVG arc spun by CSS
 * (`form.css`, scoped under `.mass-tort-form`). Kept inline so the hero owns its own feedback UI
 * without pulling in an icon dependency.
 */
function HandoffSpinner() {
  return (
    <svg data-mass-tort-spinner="" viewBox="0 0 50 50" role="status" aria-label="Loading">
      <circle cx="25" cy="25" r="20" />
    </svg>
  );
}
